// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { appStore } from '@advisor-app/store/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { i18n } from '@shared/ui-kit';
import { isNullOrUndefined } from '@shared/utils';

const validateNumber = (value: number | undefined | null) => {
  if (isNullOrUndefined(value)) return 0;

  if (isNaN(value)) return 0;

  if (!isFinite(value)) return 0;

  return value;
};

export const formatToMoney = (
  value: number | undefined | null,
  currency?: string | null,
  digits = 2,
  minimumFractionDigits = 0,
) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const validatedNumber = validateNumber(value);

  switch (currency) {
    case null:
    case '':
    case 'N.A':
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: digits,
        minimumFractionDigits: minimumFractionDigits,
        useGrouping: true,
      }).format(validatedNumber);

    default: {
      const currentCurrency =
        currency ?? selectAccountCurrency(appStore.getState());

      const options: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: currentCurrency,
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: digits,
        currencyDisplay: 'narrowSymbol',
        useGrouping: true,
      };

      const formatter = new Intl.NumberFormat(locale, options);

      return formatter.format(validatedNumber);
    }
  }
};

export const formatToPercent = (value: number | undefined | null, digits = 2) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const validatedNumber = validateNumber(value);

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    useGrouping: true,
  }).format(validatedNumber);
};
